import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import {
  overhangClippingEdgeNew,
  type OverhangClippingCorner,
  type OverhangClippingEdge,
  overhangClippingCornerNew,
  OverhangEdgeOverlap,
} from "building-types/roofs/roofOverhangClippingTypes";
import {
  RoofPerimeterRailType,
  RoofRailCornerType,
  type RoofPerimeterRail,
} from "building-types/roofs/roofPerimeterTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { GableRoofType, RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceByNPoints, faceOrNullMove, faceOrNullReverse, facesByFourPoints } from "geom/brep";
import {
  addConvexPlanesToBSPLeaf,
  addPolygonsToBSP,
  buildPolygonByFourPoints,
  buildPolygonsFromFaces,
  polygonBSPNew,
} from "geom/bsp";
import { clipFaces, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PlaneBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { intersectLine2Line2, CurveCurveRangeIntersection } from "geom/intersectLine2Line2";
import { line2ProjectPt, line2PerpDistance } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import {
  PlaneSide,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProject3,
  planeProjectPolyLine3,
  polyLine2OnPlane,
  vec3PlaneSide,
} from "geom/plane";
import { planeBSPByPointAndNormal, planeBSPByThreePoints } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2EdgeCount,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2Simplify,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByThreePoints,
  polyLine3ByTwoPoints,
  polyLine3ConcatAll,
  polyLine3Copy,
  polyLine3CopyReversed,
  polyLine3EdgeCount,
  polyLine3EdgeEnd,
  polyLine3EdgeStart,
  polyLine3EdgeTangent,
  polyLine3Length,
  polyLine3Move,
  polyLine3Simplify,
  polyLine3StartPoint,
  polyLine3VertexCount,
} from "geom/polyLine3";
import { ray2DotProject } from "geom/ray2";
import { vec2Dot, vec2Add, vec2DistSquared } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Cross, vec3Dot, vec3Negate } from "geom/vec3";
import type { RBush3D } from "rbush-3d";
import { getRoofPerimeterRailStartRafterDir, getRoofPerimeterWallRail } from "./roofPerimeterRails";
import { prepareRoofPerimeterRails } from "./roofPerimeterRailsPreparation";

const OVERHANG_VOLUME_SIZE = 100;
const OVERHANG_VOLUME_OFFSET = 0.01;
const OVERHANG_VOLUME_OFFSET_Z = -2 * MULTIGABLE_BSP_EPSILON;
const EDGE_ALIGNED_TOLERANCE = 0.5;
const LEGACY_OVERHANG_TOLERANCE = 0.5;
const OVERHANG_TOLERANCE = 0.01;

export interface RoofPerimeterInnerRail {
  readonly innerRail: PolyLine3;
  readonly hasStartCorner: boolean;
  readonly hasEndCorner: boolean;
}

function buildVolume(segmentBSP: NodeBSP | null, faces: Face[]): NodeBSP | null {
  // clip roof top faces
  const clippedTopFaces = clipFaces({
    faces,
    options: {
      clippingNodes: [clippingNodeNew(segmentBSP, true)],
      epsilon: MULTIGABLE_BSP_EPSILON,
    },
  });

  // build final polygons
  const polygons: PolygonBSP[] = [];
  for (const face of clippedTopFaces) {
    const { plane, loops } = face;
    if (loops.length === 0) {
      continue;
    }

    // cap polygons
    polygons.push(...buildPolygonsFromFaces({ faces: [face] }));

    // side polygons
    const loop3 = polyLine2OnPlane(plane, loops[0]);
    for (let i = 0, l = polyLine3EdgeCount(loop3); i < l; i++) {
      const [ax, ay, az] = polyLine3EdgeStart(loop3, i);
      const [bx, by, bz] = polyLine3EdgeEnd(loop3, i);

      const edgePlane = planeBSPByThreePoints(ax, ay, az, bx, by, bz, bx, by, 0);

      if (!edgePlane) {
        continue;
      }

      polygons.push(polygonBSPNew(0, [ax, ay, az, bx, by, bz, bx, by, 0, ax, ay, 0], edgePlane));
    }
  }

  // build finalvolume
  return addPolygonsToBSP(null, null, polygons, MULTIGABLE_BSP_EPSILON);
}

function buildTopFaces(rail: PolyLine3, edgePlane: Plane, overhang: number): Face[] {
  const [nX, nY, nZ] = planeNormal(edgePlane);

  const innerOffsetX = -nX * overhang;
  const innerOffsetY = -nY * overhang;
  const innerOffsetZ = -nZ * overhang;

  const offsetX = nX * OVERHANG_VOLUME_SIZE;
  const offsetY = nY * OVERHANG_VOLUME_SIZE;
  const offsetZ = nZ * OVERHANG_VOLUME_SIZE;

  const innerRail = polyLine3Move(polyLine3Copy(rail), innerOffsetX, innerOffsetY, innerOffsetZ);
  const outerRail = polyLine3Move(polyLine3Copy(innerRail), offsetX, offsetY, offsetZ);

  const topFaces: Face[] = [];
  for (let i = 0, l = polyLine3EdgeCount(innerRail); i < l; i++) {
    const [ax, ay, az] = polyLine3EdgeStart(outerRail, i);
    const [bx, by, bz] = polyLine3EdgeEnd(outerRail, i);
    const [cx, cy, cz] = polyLine3EdgeEnd(innerRail, i);
    const [dx, dy, dz] = polyLine3EdgeStart(innerRail, i);

    topFaces.push(...facesByFourPoints(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz));
  }

  return topFaces;
}

function buildCornerVolume(
  prevRail: PolyLine3,
  nextRail: PolyLine3,
  prevEdgePlane: Plane,
  nextEdgePlane: Plane,
  prevEdge: PolyLine2,
  nextEdge: PolyLine2,
  prevOverhang: number,
  nextOverhang: number,
): NodeBSP | null {
  const segmentPlanes: PlaneBSP[] = [];

  const [startTangentX, startTangentY] = polyLine2EdgeTangent(prevEdge, polyLine2EdgeCount(prevEdge) - 1);
  const [endTangentX, endTangentY] = polyLine2EdgeTangent(nextEdge, 0);

  const startNormalX = startTangentY;
  const startNormalY = -startTangentX;

  const endNormalX = endTangentY;
  const endNormalY = -endTangentX;

  const [innerX, innerY] = polyLine2Start(nextEdge);
  const [, , outerZ] = polyLine3StartPoint(nextRail);

  // start plane
  segmentPlanes.push(planeBSPByPointAndNormal(innerX, innerY, 0, -startTangentX, -startTangentY, 0));

  // end plane
  segmentPlanes.push(planeBSPByPointAndNormal(innerX, innerY, 0, endTangentX, endTangentY, 0));

  // build segment BSP
  const segmentBSP = addConvexPlanesToBSPLeaf(segmentPlanes);

  // build top faces
  const topFaces = [
    ...buildTopFaces(prevRail, prevEdgePlane, prevOverhang),
    ...buildTopFaces(nextRail, nextEdgePlane, nextOverhang),
    ...facesByFourPoints(
      innerX,
      innerY,
      outerZ,
      innerX + startNormalX * OVERHANG_VOLUME_SIZE,
      innerY + startNormalY * OVERHANG_VOLUME_SIZE,
      outerZ,
      innerX + startNormalX * OVERHANG_VOLUME_SIZE + endNormalX * OVERHANG_VOLUME_SIZE,
      innerY + startNormalY * OVERHANG_VOLUME_SIZE + endNormalY * OVERHANG_VOLUME_SIZE,
      outerZ,
      innerX + endNormalX * OVERHANG_VOLUME_SIZE,
      innerY + endNormalY * OVERHANG_VOLUME_SIZE,
      outerZ,
    ),
  ];

  // apply offset
  topFaces.forEach((face) => faceOrNullMove(face, 0, 0, OVERHANG_VOLUME_OFFSET_Z));

  // build final volume
  return buildVolume(segmentBSP, topFaces);
}

function buildEdgeVolume(
  useRailNormals: boolean,
  roofPerimeterRail: RoofPerimeterRail,
  wallRail: PolyLine3,
  hasStartNeighbor: boolean,
  hasEndNeighbor: boolean,
  innerOffsetZ: number = 0,
): NodeBSP | null {
  if (polyLine3Length(wallRail) < DIST_EPSILON) {
    return null;
  }

  const { rail, edgePlane, overhang, normals, rafterDirections, exteriorWallOffsetWithSheathing, fasciaBoardSize } =
    roofPerimeterRail;

  // calculate the overhang offset, including an epsilon offset to avoid zero thickness roof top faces
  const overhangOffset = overhang - exteriorWallOffsetWithSheathing + OVERHANG_VOLUME_OFFSET;

  // build wall start/end planes
  const [wallStartX, wallStartY, wallStartZ] = polyLine3StartPoint(wallRail);
  const [wallEndX, wallEndY, wallEndZ] = polyLine3EdgeEnd(wallRail, polyLine3EdgeCount(wallRail) - 1);

  const [nX, nY, nZ] = planeNormal(edgePlane);

  const leftWallPlane = planeNew(wallStartX, wallStartY, wallStartZ, nX, nY, nZ, 0, 0, 1);
  const rightWallPlane = planeNew(wallEndX, wallEndY, wallEndZ, -nX, -nY, -nZ, 0, 0, 1);

  const [leftNormalX, leftNormalY, leftNormalZ] = planeNormal(leftWallPlane);

  // discard invalid wall segments
  if (
    vec3PlaneSide(
      wallEndX,
      wallEndY,
      wallEndZ,
      wallStartX,
      wallStartY,
      wallStartZ,
      leftNormalX,
      leftNormalY,
      leftNormalZ,
    ) !== PlaneSide.BACK
  ) {
    return null;
  }

  // build top rail
  const topRail = useRailNormals
    ? offsetTopRail(polyLine3Copy(rail), normals, rafterDirections, edgePlane, -overhangOffset)
    : polyLine3Move(polyLine3Copy(rail), -nX * overhangOffset, -nY * overhangOffset, -nZ * overhangOffset);

  // build bottom rail
  const bottomRail = polyLine3Move(polyLine3Copy(topRail), 0, 0, -fasciaBoardSize);

  // build referece points
  const [leftBottomX, leftBottomY, leftBottomZ] = polyLine3StartPoint(bottomRail);
  const [leftMidX, leftMidY, leftMidZ] = hasStartNeighbor
    ? planeProject3(leftWallPlane, leftBottomX, leftBottomY, leftBottomZ)
    : [leftBottomX, leftBottomY, leftBottomZ];

  const [rightBottomX, rightBottomY, rightBottomZ] = polyLine3EdgeEnd(bottomRail, polyLine3EdgeCount(bottomRail) - 1);
  const [rightMidX, rightMidY, rightMidZ] = hasEndNeighbor
    ? planeProject3(rightWallPlane, rightBottomX, rightBottomY, rightBottomZ)
    : [rightBottomX, rightBottomY, rightBottomZ];

  // build left and right rails
  const leftRail = polyLine3ByThreePoints(
    leftMidX,
    leftMidY,
    0,
    leftMidX,
    leftMidY,
    leftMidZ,
    leftBottomX,
    leftBottomY,
    leftBottomZ,
  );

  const rightRail = polyLine3ByThreePoints(
    rightBottomX,
    rightBottomY,
    rightBottomZ,
    rightMidX,
    rightMidY,
    rightMidZ,
    rightMidX,
    rightMidY,
    0,
  );

  // build the volume
  return buildEdgeVolumeFromRails(
    useRailNormals,
    edgePlane,
    overhang,
    normals,
    rafterDirections,
    leftRail,
    rightRail,
    topRail,
    innerOffsetZ,
  );
}

function buildExtendedEdgeVolume(
  useRailNormals: boolean,
  roofPerimeterRail: RoofPerimeterRail,
  innerOffsetZ: number = 0,
): NodeBSP | null {
  const {
    baseRail,
    edgePlane,
    overhang,
    baseNormals,
    baseRafterDirections,
    exteriorWallOffsetWithSheathing,
    fasciaBoardSize,
  } = roofPerimeterRail;
  const overhangOffset = overhang - exteriorWallOffsetWithSheathing;

  const [nX, nY, nZ] = planeNormal(edgePlane);

  // build top rail
  const topRail = useRailNormals
    ? offsetTopRail(polyLine3Copy(baseRail), baseNormals, baseRafterDirections, edgePlane, -overhangOffset)
    : polyLine3Move(polyLine3Copy(baseRail), -nX * overhangOffset, -nY * overhangOffset, -nZ * overhangOffset);

  // build bottom rail
  const bottomRail = polyLine3Move(polyLine3Copy(topRail), 0, 0, -fasciaBoardSize);

  // build referece points
  const [leftBottomX, leftBottomY, leftBottomZ] = polyLine3StartPoint(bottomRail);
  const [rightBottomX, rightBottomY, rightBottomZ] = polyLine3EdgeEnd(bottomRail, polyLine3EdgeCount(bottomRail) - 1);

  // build left and right rails
  const leftRail = polyLine3ByTwoPoints(leftBottomX, leftBottomY, 0, leftBottomX, leftBottomY, leftBottomZ);
  const rightRail = polyLine3ByTwoPoints(rightBottomX, rightBottomY, rightBottomZ, rightBottomX, rightBottomY, 0);

  // build the volume
  return buildEdgeVolumeFromRails(
    useRailNormals,
    edgePlane,
    overhang,
    baseNormals,
    baseRafterDirections,
    leftRail,
    rightRail,
    topRail,
    innerOffsetZ,
  );
}

function getOffsetDir(vertexIndex: number, normals: Vec3[], rafterDirections: Vec3[]): Vec3 {
  const prevI = vertexIndex - 1;

  // start vertex uses the start rafter direction (but negated)
  if (prevI < 0) {
    const rafterDirection = rafterDirections[0];
    return vec3Negate(rafterDirection[0], rafterDirection[1], rafterDirection[2]);
  }

  // end vertex uses the end rafter direction (but negated)
  if (vertexIndex >= normals.length) {
    const rafterDirection = rafterDirections[vertexIndex - 1];
    return vec3Negate(rafterDirection[0], rafterDirection[1], rafterDirection[2]);
  }

  // middle vertices use the direction of the intersection of the previous and next faces
  // This can be obtained from the cross product of the previous and next normals
  const prevNormal = normals[prevI];
  const nextNormal = normals[vertexIndex];

  return vec3Cross(prevNormal[0], prevNormal[1], prevNormal[2], nextNormal[0], nextNormal[1], nextNormal[2]);
}

function getTopPointOffset(
  vertexIndex: number,
  normals: Vec3[],
  rafterDirections: Vec3[],
  edgePlane: Plane,
  offsetAlongEdgeNormal: number,
): Vec3 {
  // get the direction of movement
  const [dirX, dirY, dirZ] = getOffsetDir(vertexIndex, normals, rafterDirections);

  // get the normal of the edge plane
  const [nX, nY, nZ] = planeNormal(edgePlane);

  // transform the offset along the edge normal to the offset along the direction of movement
  const dot = vec3Dot(dirX, dirY, dirZ, nX, nY, nZ);
  const finalOffset = offsetAlongEdgeNormal / dot;

  // return the offset along the direction of movement
  return [dirX * finalOffset, dirY * finalOffset, dirZ * finalOffset];
}

function offsetTopRail(
  topRail: PolyLine3,
  normals: Vec3[],
  rafterDirections: Vec3[],
  edgePlane: Plane,
  offset: number,
): PolyLine3 {
  if (!(polyLine3EdgeCount(topRail) === normals.length)) {
    console.assert(false, "topRail and normals must have the same number of edges");
  }

  if (!(polyLine3EdgeCount(topRail) === rafterDirections.length)) {
    console.assert(false, "topRail and rafterDirections must have the same number of edges");
  }

  for (let i = 0, l = polyLine3VertexCount(topRail); i < l; i++) {
    const [offsetX, offsetY, offsetZ] = getTopPointOffset(i, normals, rafterDirections, edgePlane, offset);

    topRail[i * 3 + 0] += offsetX;
    topRail[i * 3 + 1] += offsetY;
    topRail[i * 3 + 2] += offsetZ;
  }

  return topRail;
}

function buildEdgeVolumeFromRails(
  useRailNormals: boolean,
  edgePlane: Plane,
  overhang: number,
  normals: Vec3[],
  rafterDirections: Vec3[],
  leftRail: PolyLine3,
  rightRail: PolyLine3,
  topRail: PolyLine3,
  innerOffsetZ: number = 0,
): NodeBSP | null {
  const [nX, nY, nZ] = planeNormal(edgePlane);

  // build inner profile
  const innerProfileRails = [leftRail, topRail, rightRail];
  const innerProfile = polyLine3Simplify(polyLine3ConcatAll(innerProfileRails, true));
  polyLine3Move(innerProfile, 0, 0, OVERHANG_VOLUME_OFFSET_Z);

  // offset the left and right rails by the same amount of the start and end points of the top rail respectively
  const [leftOffsetX, leftOffsetY, leftOffsetZ] = useRailNormals
    ? getTopPointOffset(0, normals, rafterDirections, edgePlane, overhang)
    : [nX * overhang, nY * overhang, nZ * overhang];

  const [rightOffsetX, rightOffsetY, rightOffsetZ] = useRailNormals
    ? getTopPointOffset(polyLine3VertexCount(topRail) - 1, normals, rafterDirections, edgePlane, overhang)
    : [nX * overhang, nY * overhang, nZ * overhang];

  const overhangLeftRail = polyLine3Move(polyLine3Copy(leftRail), leftOffsetX, leftOffsetY, leftOffsetZ);
  const overhangRightRail = polyLine3Move(polyLine3Copy(rightRail), rightOffsetX, rightOffsetY, rightOffsetZ);

  // keep bottom points at the same height
  overhangLeftRail[2] = leftRail[2];
  overhangRightRail[overhangRightRail.length - 1] = rightRail[rightRail.length - 1];

  // offset the top rail by the overhang amount
  const overhangTopRail = useRailNormals
    ? offsetTopRail(polyLine3Copy(topRail), normals, rafterDirections, edgePlane, overhang)
    : polyLine3Move(polyLine3Copy(topRail), nX * overhang, nY * overhang, nZ * overhang);

  // build the profile at the overhang
  const overhangProfile = polyLine3Simplify(
    polyLine3ConcatAll([overhangLeftRail, overhangTopRail, overhangRightRail], true),
  );

  // build outer profile
  const offsetX = nX * OVERHANG_VOLUME_SIZE;
  const offsetY = nY * OVERHANG_VOLUME_SIZE;
  const offsetZ = nZ * OVERHANG_VOLUME_SIZE;

  const outerProfile = polyLine3Move(polyLine3Copy(overhangProfile), offsetX, offsetY, offsetZ);

  // apply vertical inner offset
  polyLine3Move(innerProfile, 0, 0, innerOffsetZ);

  // build cap polygons
  const polygons: PolygonBSP[] = [];

  const innerFace = faceOrNullReverse(faceByNPoints(polyLine3CopyReversed(innerProfile)));
  buildPolygonsFromFaces({ faces: [innerFace], polygons });

  const outerFace = faceByNPoints(polyLine3CopyReversed(outerProfile));
  buildPolygonsFromFaces({ faces: [outerFace], polygons });

  // build the volume side polygons
  const buildAndAddPolygon = (pl1: PolyLine3, pl2: PolyLine3, edgeIndex: number) => {
    const [x1, y1, z1] = polyLine3EdgeStart(pl1, edgeIndex);
    const [x2, y2, z2] = polyLine3EdgeEnd(pl1, edgeIndex);
    const [x3, y3, z3] = polyLine3EdgeEnd(pl2, edgeIndex);
    const [x4, y4, z4] = polyLine3EdgeStart(pl2, edgeIndex);

    const polygon = buildPolygonByFourPoints(x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4);

    if (!polygon) {
      return;
    }

    polygons.push(polygon);
  };

  for (let i = 0, l = polyLine3EdgeCount(innerProfile); i < l; i++) {
    buildAndAddPolygon(overhangProfile, innerProfile, i);
    buildAndAddPolygon(outerProfile, overhangProfile, i);
  }

  // build final volume
  return addPolygonsToBSP(null, null, polygons, MULTIGABLE_BSP_EPSILON);
}

export function buildOverhangClipping(
  subRoofs: Table<GeneratedRoof>,
  clipContext: MultiGableRoofClipContext,
): RWTable<GeneratedRoof> {
  const buildOverhangClippingStep = (subRoofs: Table<GeneratedRoof>): Table<GeneratedRoof> => {
    const { roofPerimeterRailsTable, rtree } = prepareRoofPerimeterRails({
      subRoofs,
      clipContext,
      collectOverhangClipping,
      ignoreExtensions: true,
    });

    const newSubRoofs: RWTable<GeneratedRoof> = {};
    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      const { roofTopFaces } = subRoof;
      const overhangClippingEdges = buildSubRoofOverhangClipping(
        roofPerimeterRailsTable[subRoofId],
        roofTopFaces,
        rtree,
      );

      newSubRoofs[subRoofId] = {
        ...subRoof,
        overhangClippingEdges,
      };
    }

    return newSubRoofs;
  };

  // we need to perform this as a 2 step process:
  // - First we do a basic clipping to roof perimeter rails. This clipping doesn't include the overhang clipping as
  //   it is not yet generated. With the resulting roof perimeter rails we generate the basic version of the overhang
  ///  clipping volumes.
  // - Then we do a second pass. This pass will clip again the roof perimeter rails, but this time using the basic
  //   overhang clipping volumes. From them, we will generate the final overhang clipping volumes.

  // step 1: build basic overhang clipping
  let newSubRoofs = buildOverhangClippingStep(subRoofs);

  // step2: build overhang clipping using the basic overhang clipping
  newSubRoofs = buildOverhangClippingStep(newSubRoofs);

  return newSubRoofs;
}

function isEaveToRakeCorner(roofPerimeterRail: RoofPerimeterRail, isEnd: boolean): boolean {
  const { rail, nextPerimeterRail, prevPerimeterRail } = roofPerimeterRail;

  const [, , tz] = polyLine3EdgeTangent(rail, isEnd ? polyLine3EdgeCount(rail) - 1 : 0);

  if (Math.abs(tz) > DIST_EPSILON) {
    return false;
  }

  const neighborRail = isEnd ? nextPerimeterRail?.rail : prevPerimeterRail?.rail;
  if (!neighborRail) {
    return true;
  }

  const [, , neighborTz] = polyLine3EdgeTangent(neighborRail, isEnd ? 0 : polyLine3EdgeCount(neighborRail) - 1);

  return Math.abs(neighborTz) > DIST_EPSILON;
}

function buildSubRoofOverhangClipping(
  roofPerimeterRails: RoofPerimeterRail[],
  roofTopFaces: RoofFace[],
  rtree: RBush3D,
): OverhangClippingEdge[] {
  const overhangClippingEdges: OverhangClippingEdge[] = [];
  const groundPlane = planeNewXY();

  for (const roofPerimeterRail of roofPerimeterRails) {
    const { type, rail, baseRail, baseEdge, side, edgePlane, overhang, isStartEdge, isEndEdge } = roofPerimeterRail;
    const [, , startHeight] = polyLine3StartPoint(baseRail);
    const [, , endHeight] = polyLine3EdgeEnd(baseRail, polyLine3EdgeCount(baseRail) - 1);

    // special case for the high edge of the shed roof (negative edge slope)
    // we move down the inner edge of the volume to avoid some gaps on the parent roof
    const [, , rafterDirZ] = getRoofPerimeterRailStartRafterDir(roofPerimeterRail);
    const innerOffsetZ = rafterDirZ > DIST_EPSILON ? -overhang * rafterDirZ : 0;

    const {
      rail: wallRail,
      startCornerType,
      endCornerType,
      prevRoofPerimeterRail,
      nextRoofPerimeterRail,
    } = getRoofPerimeterWallRail({ roofPerimeterRail, rtree, roofTopFaces });

    const hasStartOutsideCorner = startCornerType === RoofRailCornerType.OUTSIDE;
    const hasEndOutsideCorner = endCornerType === RoofRailCornerType.OUTSIDE;

    const edge = offsetPolyLine2(polyLine2Simplify(planeProjectPolyLine3(groundPlane, rail)), -overhang);

    const useRailNormals = isEaveToRakeCorner(roofPerimeterRail, false) && isEaveToRakeCorner(roofPerimeterRail, true);

    // build edge volume
    const edgeVolume = buildEdgeVolume(
      useRailNormals,
      roofPerimeterRail,
      wallRail,
      !!prevRoofPerimeterRail,
      !!nextRoofPerimeterRail,
      innerOffsetZ,
    );

    const extendedEdgeVolume = buildExtendedEdgeVolume(useRailNormals, roofPerimeterRail, innerOffsetZ);

    let prevCorner: OverhangClippingCorner | undefined;
    let nextCorner: OverhangClippingCorner | undefined;

    // add start coner volume if needed
    if (hasStartOutsideCorner) {
      assertIsNotUndefined(prevRoofPerimeterRail);
      const {
        rail: prevRail,
        overhang: prevOverhang,
        edgePlane: prevEdgePlane,
        baseEdge: prevBaseEdge,
      } = prevRoofPerimeterRail;

      const prevCornerVolume = buildCornerVolume(
        prevRail,
        rail,
        prevEdgePlane,
        edgePlane,
        prevBaseEdge,
        edge,
        prevOverhang,
        overhang,
      );

      prevCorner = overhangClippingCornerNew(prevBaseEdge, edge, prevOverhang, overhang, prevCornerVolume);
    }

    // add end corner volume if needed
    if (hasEndOutsideCorner) {
      assertIsNotUndefined(nextRoofPerimeterRail);
      const {
        rail: nextRail,
        overhang: nextOverhang,
        edgePlane: nextEdgePlane,
        baseEdge: nextBaseEdge,
      } = nextRoofPerimeterRail;

      const nextCornerVolume = buildCornerVolume(
        rail,
        nextRail,
        edgePlane,
        nextEdgePlane,
        edge,
        nextBaseEdge,
        overhang,
        nextOverhang,
      );

      nextCorner = overhangClippingCornerNew(edge, nextBaseEdge, overhang, nextOverhang, nextCornerVolume);
    }

    overhangClippingEdges.push(
      overhangClippingEdgeNew(
        side,
        type,
        isStartEdge,
        isEndEdge,
        edge,
        baseEdge,
        overhang,
        startHeight,
        endHeight,
        edgeVolume,
        extendedEdgeVolume,
        prevCorner,
        nextCorner,
      ),
    );
  }

  return overhangClippingEdges;
}

function isChildRoof(subRoof: GeneratedRoof, otherSubRoof: GeneratedRoof): boolean {
  const { id, roofSettings } = subRoof;
  const { boundaryType = RoofBoundaryType.BOUNDING_BOX } = roofSettings;

  if (boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER) {
    return false;
  }

  const { roofVolume: otherRoofVolume, roofType: otherRoofType } = otherSubRoof;
  const { neighborRoofs: otherNeighborRoofs } = otherRoofVolume;

  if (otherRoofType !== GableRoofType.DEFAULT) {
    return false;
  }

  const {
    startNeighbor: otherStartNeighbor,
    endNeighbor: otherEndNeighbor,
    leftNeighbor: otherLeftNeighbor,
    rightNeighbor: otherRightNeighbor,
  } = otherNeighborRoofs;

  return (
    id === otherStartNeighbor.roofId ||
    id === otherEndNeighbor.roofId ||
    id === otherLeftNeighbor.roofId ||
    id === otherRightNeighbor.roofId
  );
}

interface CollectOverhangClippingOptions {
  subRoof: GeneratedRoof;
  subRoofs: Table<GeneratedRoof>;
  isWallClipping?: boolean;
  isEaveClipping?: boolean;
  filter?: (subRoof: GeneratedRoof, side: RoofSide, otherSubRoof: GeneratedRoof, otherSide: RoofSide) => boolean;
}

export function collectOverhangClipping(options: CollectOverhangClippingOptions): (NodeBSP | null)[] {
  const groundPlane = planeNewXY();

  const getEdgeWithExtension = ({ rail }: RoofPerimeterRail): PolyLine2 => {
    return polyLine2Simplify(planeProjectPolyLine3(groundPlane, rail));
  };

  const isCornerAlignedWithOther = (
    edge: PolyLine2,
    edgeWithExtension: PolyLine2,
    overhang: number,
    otherCorner: OverhangClippingCorner | undefined,
    isEnd: boolean,
    overhangAlignedTolerance: number,
    legacyRoof: boolean,
    isChild: boolean,
  ): boolean => {
    if (!otherCorner) {
      return false;
    }

    const otherEdge = isEnd ? otherCorner.nextEdge : otherCorner.prevEdge;
    const otherOverhang = isEnd ? otherCorner.nextOverhang : otherCorner.prevOverhang;

    return isEdgeAlignedWithOther(
      edge,
      edgeWithExtension,
      otherEdge,
      overhang,
      otherOverhang,
      overhangAlignedTolerance,
      legacyRoof,
      isChild,
    );
  };

  const { subRoof, subRoofs, filter } = options;
  const { roofVolume, srcRoofPerimeterRails, roofType, legacyRoof } = subRoof;
  const { baseHeight, exactEdges, isStartFullyTruncated, isEndFullyTruncated } = roofVolume;
  const edgeCount = exactEdges.length;
  const isShedRoof = roofType !== GableRoofType.DEFAULT;

  // check against all the other sub-roofs...
  const overhangClippingBSPs: (NodeBSP | null)[] = [];
  for (const otherSubRoofId in subRoofs) {
    const otherSubRoof = subRoofs[otherSubRoofId];

    // filter out the same sub-roof
    if (subRoof.index === otherSubRoof.index) {
      continue;
    }

    // iterate all the edges of the sub-roof...
    const {
      roofVolume: otherRoofVolume,
      overhangClippingEdges: otherClippingEdges,
      isDormer: otherIsDormer,
    } = otherSubRoof;

    const {
      isStartFullyTruncated: isOtherStartFullyTruncated,
      isEndFullyTruncated: isOtherEndFullyTruncated,
      baseHeight: otherBaseHeight,
    } = otherRoofVolume;

    let overhangAlignedTolerance = OVERHANG_TOLERANCE;
    if (legacyRoof) {
      overhangAlignedTolerance = otherIsDormer ? OVERHANG_TOLERANCE : LEGACY_OVERHANG_TOLERANCE;
    }

    const isAbove = baseHeight + DIST_EPSILON < otherBaseHeight;

    for (let i = 0, l = srcRoofPerimeterRails.length; i < l; i++) {
      const srcRoofPerimeterRail = srcRoofPerimeterRails[i];
      const prevSrcRoofPerimeterRail = srcRoofPerimeterRails[(i + l - 1) % l];
      const nextSrcRoofPerimeterRail = srcRoofPerimeterRails[(i + 1) % l];

      const { edgeIndex, baseEdge, side, overhang, isStartEdge, isEndEdge, type, baseRail } = srcRoofPerimeterRail;
      const [, , startHeight] = polyLine3StartPoint(baseRail);
      const [, , endHeight] = polyLine3EdgeEnd(baseRail, polyLine3EdgeCount(baseRail) - 1);
      const edgeWithExtension = getEdgeWithExtension(srcRoofPerimeterRail);

      const isEdgeValidForCascade =
        (side === RoofSide.START && !isStartFullyTruncated) || (side === RoofSide.END && !isEndFullyTruncated);

      // check against all the other sub-roof clipping edges...
      for (const otherClippingEdge of otherClippingEdges) {
        const {
          edge: otherEdge,
          baseEdge: otherBaseEdge,
          side: otherSide,
          overhang: otherOverhang,
          prevCorner: otherPrevCorner,
          nextCorner: otherNextCorner,
          isStartEdge: isOtherStartEdge,
          isEndEdge: isOtherEndEdge,
          type: otherType,
          startHeight: otherStartHeight,
          endHeight: otherEndHeight,
        } = otherClippingEdge;

        const isChild = isChildRoof(subRoof, otherSubRoof);

        // to mimic the behavior from the old overhang clipping
        const orderFilterEnabled = legacyRoof
          ? isAbove
          : type === RoofPerimeterRailType.RAKE && otherType === RoofPerimeterRailType.RAKE;

        if (
          orderFilterEnabled &&
          (isStartEdge || isEndEdge) &&
          (isOtherStartEdge || isOtherEndEdge) &&
          !(subRoof.index < otherSubRoof.index || subRoof.roofType !== GableRoofType.DEFAULT)
        ) {
          continue;
        }

        // custom filter
        if (filter && !filter(subRoof, side, otherSubRoof, otherSide)) {
          continue;
        }

        const edgeOverlap = getEdgeOverlap(baseEdge, otherBaseEdge);
        const otherEdgeOverlap = getEdgeOverlap(otherBaseEdge, baseEdge);
        const isFullyContained = edgeOverlap === OverhangEdgeOverlap.FULL;
        const isOtherFullyContained = otherEdgeOverlap === OverhangEdgeOverlap.FULL;
        const isOtherEdgeValidForCascade =
          (otherSide === RoofSide.START && !isOtherStartFullyTruncated) ||
          (otherSide === RoofSide.END && !isOtherEndFullyTruncated);

        // check if the neighbor edges are aligned
        const { edgeIndex: prevEdgeIndex, baseEdge: prevEdge, overhang: prevOverhang } = prevSrcRoofPerimeterRail;
        const { edgeIndex: nextEdgeIndex, baseEdge: nextEdge, overhang: nextOverhang } = nextSrcRoofPerimeterRail;

        let isPrevAligned = false;
        let isNextAligned = false;

        const isPrevValid = (prevEdgeIndex + 1) % edgeCount === edgeIndex;
        const isNextValid = (edgeIndex + 1) % edgeCount === nextEdgeIndex;

        // we must be sure this is the previous edge
        if (isPrevValid) {
          const prevEdgeWithExtension = getEdgeWithExtension(prevSrcRoofPerimeterRail);

          isPrevAligned = isCornerAlignedWithOther(
            prevEdge,
            prevEdgeWithExtension,
            prevOverhang,
            otherPrevCorner,
            false,
            overhangAlignedTolerance,
            legacyRoof,
            isChild,
          );
        }

        // we must be sure this is the next edge
        if (isNextValid) {
          const nextEdgeWithExtension = getEdgeWithExtension(nextSrcRoofPerimeterRail);

          isNextAligned = isCornerAlignedWithOther(
            nextEdge,
            nextEdgeWithExtension,
            nextOverhang,
            otherNextCorner,
            true,
            overhangAlignedTolerance,
            legacyRoof,
            isChild,
          );
        }

        // filter out invalid edges: our edge is fully contained inside the other edge,
        // both neighbor edges are not alligned, and previous and next edges are poiting to the same side
        if (isFullyContained && isPrevValid && isNextValid && !isPrevAligned && !isNextAligned) {
          const [prevTanX, prevTanY] = polyLine2EdgeTangent(
            prevSrcRoofPerimeterRail.baseEdge,
            polyLine2EdgeCount(prevSrcRoofPerimeterRail.baseEdge) - 1,
          );
          const [nextTanX, nextTanY] = polyLine2EdgeTangent(nextSrcRoofPerimeterRail.baseEdge, 0);

          if (vec2Dot(prevTanX, prevTanY, nextTanX, nextTanY) > DIST_EPSILON) {
            continue;
          }
        }

        // cascade roof filer
        if (!isShedRoof && isEdgeValidForCascade && isOtherEdgeValidForCascade && isFullyContained) {
          continue;
        }

        // check if the edges are aligned
        const isAligned = isEdgeAlignedWithOther(
          baseEdge,
          edgeWithExtension,
          otherEdge,
          overhang,
          otherOverhang,
          overhangAlignedTolerance,
          legacyRoof,
          isChild,
        );

        if (isAligned) {
          const addExtendedEdgeVolume =
            isOtherFullyContained &&
            type === RoofPerimeterRailType.EAVE &&
            otherType === RoofPerimeterRailType.RAKE &&
            Math.abs(startHeight - otherStartHeight) < DIST_EPSILON &&
            Math.abs(endHeight - otherEndHeight) < DIST_EPSILON &&
            Math.abs(startHeight - endHeight) < DIST_EPSILON &&
            Math.abs(otherOverhang - overhang) < OVERHANG_TOLERANCE;

          // add the corresponding clipping volumes
          overhangClippingBSPs.push(
            addExtendedEdgeVolume ? otherClippingEdge.extendedEdgeVolume : otherClippingEdge.edgeVolume,
          );

          if (isPrevAligned) {
            assertIsNotUndefined(otherPrevCorner);
            overhangClippingBSPs.push(otherPrevCorner.volume);
          }

          if (isNextAligned) {
            assertIsNotUndefined(otherNextCorner);
            overhangClippingBSPs.push(otherNextCorner.volume);
          }
        }
      }
    }
  }

  return overhangClippingBSPs;
}

interface CollectEaveReturnOverhangClippingOptions {
  readonly edge: PolyLine2;
  readonly eaveReturnOverhang: number;
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
}

export function collectEaveReturnOverhangClipping(
  options: CollectEaveReturnOverhangClippingOptions,
): (NodeBSP | null)[] {
  const { edge, eaveReturnOverhang, subRoofs, subRoof } = options;
  const { legacyRoof } = subRoof;

  // check against all the other sub-roofs...
  const overhangClippingBSPs: (NodeBSP | null)[] = [];
  for (const otherSubRoofId in subRoofs) {
    const otherSubRoof = subRoofs[otherSubRoofId];

    // filter out the same sub-roof
    if (subRoof.index === otherSubRoof.index) {
      continue;
    }

    // iterate all the edges of the sub-roof...
    const { overhangClippingEdges: otherClippingEdges } = otherSubRoof;

    // check against all the other sub-roof clipping edges...
    for (const otherClippingEdge of otherClippingEdges) {
      const { edge: otherEdge, overhang: otherOverhang, baseEdge: otherBaseEdge } = otherClippingEdge;

      const isFullyContained = getEdgeOverlap(edge, otherBaseEdge) === OverhangEdgeOverlap.FULL;

      // filter out if it's fully contained inside the other edge,
      if (isFullyContained) {
        continue;
      }

      const isChild = isChildRoof(subRoof, otherSubRoof);

      // check if the edges are aligned
      const isAligned = isEdgeAlignedWithOther(
        edge,
        edge,
        otherEdge,
        eaveReturnOverhang,
        otherOverhang,
        EDGE_ALIGNED_TOLERANCE,
        legacyRoof,
        isChild,
      );

      if (isAligned) {
        // add the corresponding clipping volumes
        overhangClippingBSPs.push(otherClippingEdge.extendedEdgeVolume);
      }
    }
  }

  return overhangClippingBSPs;
}

function isEdgeAlignedWithOther(
  edge: PolyLine2,
  edgeWithExtension: PolyLine2,
  otherEdge: PolyLine2,
  overhang: number,
  otherOverhang: number,
  overhangAlignedTolerance: number,
  legacyRoof: boolean,
  isChild: boolean,
): boolean {
  if (!edge || !otherEdge) {
    return false;
  }

  const [normalX, normalY] = polyLine2EdgeNormal(edge, 0);
  const [otherNormalX, otherNormalY] = polyLine2EdgeNormal(otherEdge, 0);

  // check if the edges are parallel and in the same direction
  if (vec2Dot(normalX, normalY, otherNormalX, otherNormalY) < ONE_MINUS_DIST_EPSILON) {
    return false;
  }

  // check for projected overlap
  const [p0x, p0y] = line2ProjectPt(
    edgeWithExtension[0],
    edgeWithExtension[1],
    edgeWithExtension[2],
    edgeWithExtension[3],
    otherEdge[0],
    otherEdge[1],
  );
  const [p1x, p1y] = line2ProjectPt(
    edgeWithExtension[0],
    edgeWithExtension[1],
    edgeWithExtension[2],
    edgeWithExtension[3],
    otherEdge[2],
    otherEdge[3],
  );
  const projectedInt = intersectLine2Line2(
    edgeWithExtension[0],
    edgeWithExtension[1],
    edgeWithExtension[2],
    edgeWithExtension[3],
    p0x,
    p0y,
    p1x,
    p1y,
  );

  if (!projectedInt || !(projectedInt instanceof CurveCurveRangeIntersection)) {
    return false;
  }

  // check if the edges are aligned (we already now that they are parallel)
  const dist = line2PerpDistance(edge[0], edge[1], edge[2], edge[3], otherEdge[0], otherEdge[1]);
  if (dist < EDGE_ALIGNED_TOLERANCE && (overhang < otherOverhang - DIST_EPSILON || isChild || !legacyRoof)) {
    return true;
  }

  // build the ref point
  const [refX, refY] = vec2Add(edge[0], edge[1], normalX * overhang, normalY * overhang);

  // project the ref point
  const dot = ray2DotProject(otherEdge[0], otherEdge[1], otherNormalX, otherNormalY, refX, refY);

  // check it the ref point is below the overhang from the other roof
  return dot > -DIST_EPSILON && dot < otherOverhang + overhangAlignedTolerance;
}

function getEdgeOverlap(edge: PolyLine2, otherEdge: PolyLine2): OverhangEdgeOverlap {
  if (!edge || !otherEdge) {
    return OverhangEdgeOverlap.NONE;
  }

  const [normalX, normalY] = polyLine2EdgeNormal(edge, 0);
  const [otherNormalX, otherNormalY] = polyLine2EdgeNormal(otherEdge, 0);

  // check if the edges are parallel and in the same direction
  if (vec2Dot(normalX, normalY, otherNormalX, otherNormalY) < ONE_MINUS_DIST_EPSILON) {
    return OverhangEdgeOverlap.NONE;
  }

  // check for projected overlap
  const [p0x, p0y] = line2ProjectPt(otherEdge[0], otherEdge[1], otherEdge[2], otherEdge[3], edge[0], edge[1]);
  const [p1x, p1y] = line2ProjectPt(otherEdge[0], otherEdge[1], otherEdge[2], otherEdge[3], edge[2], edge[3]);
  const projectedInt = intersectLine2Line2(otherEdge[0], otherEdge[1], otherEdge[2], otherEdge[3], p0x, p0y, p1x, p1y);

  if (!projectedInt || !(projectedInt instanceof CurveCurveRangeIntersection)) {
    return OverhangEdgeOverlap.NONE;
  }

  const tolSq = EDGE_ALIGNED_TOLERANCE * EDGE_ALIGNED_TOLERANCE;
  const isFullyContained =
    vec2DistSquared(p0x, p0y, projectedInt.startx, projectedInt.starty) < tolSq &&
    vec2DistSquared(p1x, p1y, projectedInt.endx, projectedInt.endy) < tolSq;

  return isFullyContained ? OverhangEdgeOverlap.FULL : OverhangEdgeOverlap.PARTIAL;
}
